import React, { useMemo, useState } from 'react';
import { Retry } from '@components/common';
import Table from '@components/common/Table';
import { useAtom } from 'jotai';
import { humanize } from '@utils';
import useSetExport from '@components/export/useSetExport';
import { useFetchApiUsageBreakdownGroupby } from './hooks';
import { breakdownFiltersAtom, BreakdownFilter } from './atoms';
import { ALL_BREAKDOWN_DIMENSIONS, USER_NAME_DIMENSION } from './constants';
import {
  DimensionTableWrapper,
  TableWrapper,
  WidgetActions,
  StyledEllipsis,
  StyledCell,
} from '@components/dimension-panel-hz/dimension-table/styled';
import Search from '@components/dimension-panel-hz/dimension-table/Search';
import { Typography } from 'antd';

const { Title } = Typography;

/**
 * Shown for calls with no end user attached. Not an error: every Public API call is
 * authenticated by an API key, and only MCP-over-SSO-OAuth additionally identifies a
 * person, so this bucket is legitimate machine-to-machine usage.
 */
const NO_USER_LABEL = 'API key (no user)';

interface ApiUsageBreakdownTableProps {
  dimension: string;
}

const ApiUsageBreakdownTable = ({ dimension }: ApiUsageBreakdownTableProps) => {
  const { isFetching, isError, data, refetch } = useFetchApiUsageBreakdownGroupby(dimension);
  const [filters, setFilters] = useAtom(breakdownFiltersAtom);
  const [searchText, onSearch] = useState('');
  const dimensionConfig = ALL_BREAKDOWN_DIMENSIONS.find((d) => d.key === dimension);
  const label = dimensionConfig?.label || dimension;
  const isUserNameDimension = dimension === USER_NAME_DIMENSION;

  const activeFilterValue = filters.find((f) => f.field === dimension)?.value;

  const onRowClick = ({ rowData }) => {
    if (rowData.totalRow) return;
    const value = rowData[dimension];
    if (value == null) return;
    // The "no user" bucket is an absence, not a value — filtering the other tables by
    // an empty string would silently return nothing.
    if (isUserNameDimension && !value) return;
    const strValue = String(value);

    setFilters((prev) => {
      const existing = prev.find((f) => f.field === dimension && f.value === strValue);
      if (existing) {
        return prev.filter((f) => !(f.field === dimension && f.value === strValue));
      }
      const withoutSameDimension = prev.filter((f) => f.field !== dimension);
      const newFilter: BreakdownFilter = {
        field: dimension,
        key: null,
        op: 'eq',
        value: strValue,
        display: `${label}: ${strValue}`,
        originalField: dimension,
      };
      return [...withoutSameDimension, newFilter];
    });
  };

  const rowEventHandlers = {
    onClick: onRowClick,
  };

  const rowClassName = ({ rowData }) => {
    if (String(rowData[dimension]) === activeFilterValue) {
      return 'row-selected';
    }
    return '';
  };

  const columns = [
    {
      key: dimension,
      dataKey: dimension,
      title: label,
      width: 200,
      flexGrow: 1,
      resizable: true,
      cellRenderer: ({ cellData }) => (
        <StyledEllipsis>
          {isUserNameDimension && !cellData ? NO_USER_LABEL : cellData}
        </StyledEllipsis>
      ),
    },
    {
      key: 'RealtimeAPICalls',
      dataKey: 'RealtimeAPICalls',
      title: 'Real-Time',
      width: 100,
      align: 'right' as const,
      cellRenderer: ({ cellData }) => <StyledCell>{humanize(cellData) ?? '0'}</StyledCell>,
    },
    {
      key: 'HistoricalAPICalls',
      dataKey: 'HistoricalAPICalls',
      title: 'Analytics',
      width: 100,
      align: 'right' as const,
      cellRenderer: ({ cellData }) => <StyledCell>{humanize(cellData) ?? '0'}</StyledCell>,
    },
  ];

  const tableData = useMemo(() => {
    const rows = data?.data || [];
    if (!searchText) return rows;
    return rows.filter((row) => String(row[dimension]).toLowerCase().includes(searchText));
  }, [data, searchText, dimension]);

  const totalsRow = useMemo(() => {
    const totals = data?.totals || {};
    return {
      [dimension]: 'Total',
      ...totals,
      totalRow: true,
    };
  }, [data, dimension]);

  const exportData = useMemo(() => {
    const header = [label, 'Real-Time API Calls', 'Analytics API Calls'];
    const rows = tableData.map((row) => [
      isUserNameDimension && !row[dimension] ? NO_USER_LABEL : row[dimension],
      row.RealtimeAPICalls ?? 0,
      row.HistoricalAPICalls ?? 0,
    ]);
    return [header, ...rows];
  }, [tableData, dimension, label, isUserNameDimension]);

  useSetExport({
    pageKey: 'usageDashboard',
    sectionKey: `ud-api-breakdown-${dimension}`,
    widgetKey: 'groupby',
    widgetInfo: { title: `API Usage Breakdown - ${label}` },
    data: exportData,
    headers: [],
  });

  return (
    <DimensionTableWrapper style={{ height: 350 }}>
      <Title level={5} style={{ marginBottom: 4, padding: '4px 8px' }}>
        {label}
      </Title>
      <WidgetActions style={{ top: 8, right: 8 }}>
        <Search onSearch={onSearch} text={searchText} />
      </WidgetActions>
      <TableWrapper>
        <Retry
          fetchData={refetch}
          isLoading={isFetching}
          showError={isError}
          isEmpty={tableData.length === 0 && !isFetching}
          progress={true}
        >
          <Table
            fixed={true}
            data={tableData}
            frozenData={[totalsRow]}
            columns={columns}
            headerHeight={35}
            rowHeight={35}
            rowKey={dimension}
            rowEventHandlers={rowEventHandlers}
            rowClassName={rowClassName}
          />
        </Retry>
      </TableWrapper>
    </DimensionTableWrapper>
  );
};

export default ApiUsageBreakdownTable;
